import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  Timestamp,
} from 'firebase/firestore';
import {firestore} from './firebase';
import {storage} from './storage/ApiStorage';
import {DraftDTO} from '../models/event/DraftDTO';
import {CreateEventFormType, EventFields} from '../models';
import {
  HubblFilter,
  Question,
  FirestoreCollection,
  AdminInvitedEmployees,
} from 'hubbl-shared';

const DRAFTS_COLLECTION = FirestoreCollection.Drafts;

// Convert form data to DraftDTO format
const formDataToDraftDTO = (
  draftId: string,
  formData: CreateEventFormType,
  filters: HubblFilter[],
  questions: Question[],
  evaluationQuestions: Question[],
  coverImageUrl: string | null,
  existingDraft?: DraftDTO,
): DraftDTO => {
  const now = Timestamp.now();

  return {
    id: draftId,
    title: formData[EventFields.EVENT_TITLE] || '',
    created: existingDraft?.created || now,
    lastModified: now,

    // Event details
    eventTitle: formData[EventFields.EVENT_TITLE] || '',
    eventDescription: formData[EventFields.EVENT_DESCRIPTION] || '',
    eventEmoji: formData[EventFields.EVENT_EMOJI] || '',
    eventLocation: formData[EventFields.EVENT_LOCATION] || '',
    eventTags: formData[EventFields.EVENT_TAGS] || [],

    // Dates
    startDateTime: formData[EventFields.START_DATE_TIME] || null,
    endDateTime: formData[EventFields.END_DATE_TIME] || null,
    marketingStartDate: formData[EventFields.MARKETING_DATE_TIME] || null,
    deadline: formData[EventFields.DEADLINE] || null,
    evaluationEndDate: formData[EventFields.EVALUATION_END_DATE] || null,

    // Cover image
    coverImageUrl: coverImageUrl || null,

    // Reminders
    reminders: formData[EventFields.REMINDERS] || [],
    evaluationReminders: formData[EventFields.EVALUATION_REMINDERS] || [],

    // Evaluation
    isEvaluationEnabled: formData[EventFields.IS_EVALUATION_ENABLED] ?? false,
    evaluationCustomDescription:
      formData[EventFields.EVALUATION_CUSTOM_DESCRIPTION] || null,

    // Settings
    maxAttendees: formData[EventFields.MAX_ATTENDEES] ?? null,
    adminInvitedEmployees:
      formData[EventFields.INVITE_TYPE] ?? AdminInvitedEmployees.NONE,
    ticketSystemEnabled: formData[EventFields.TICKET_SYSTEM_ENABLED] ?? false,

    // Visibility and questions
    filters: filters || [],
    questions: questions || [],
    evaluationQuestions: evaluationQuestions || [],
  };
};

const saveDraft = async (
  companyId: string,
  draftId: string,
  formData: CreateEventFormType,
  filters: HubblFilter[],
  questions: Question[],
  evaluationQuestions: Question[],
  coverImageFile?: File | Blob,
): Promise<DraftDTO> => {
  // Upload image if provided
  let coverImageUrl: string | null = null;
  if (coverImageFile) {
    coverImageUrl = await storage.uploadDraftImage(
      coverImageFile,
      companyId,
      draftId,
    );
  }

  // Create draft document
  const draftData = formDataToDraftDTO(
    draftId,
    formData,
    filters,
    questions,
    evaluationQuestions,
    coverImageUrl,
  );

  // Save to Firestore
  const companyRef = doc(firestore, FirestoreCollection.Companies, companyId);
  const draftRef = doc(companyRef, DRAFTS_COLLECTION, draftId);

  await setDoc(draftRef, draftData);

  return draftData;
};

const updateDraft = async (
  companyId: string,
  draftId: string,
  formData: CreateEventFormType,
  filters: HubblFilter[],
  questions: Question[],
  evaluationQuestions: Question[],
  coverImageFile?: File | Blob,
): Promise<DraftDTO> => {
  // Get existing draft to preserve created timestamp and check for existing image
  const existingDraft = await getDraftById(companyId, draftId);

  let coverImageUrl: string | null = existingDraft?.coverImageUrl || null;

  // If new image provided, delete old one and upload new one
  if (coverImageFile) {
    if (existingDraft?.coverImageUrl) {
      await storage.deleteDraftImage(companyId, draftId);
    }
    coverImageUrl = await storage.uploadDraftImage(
      coverImageFile,
      companyId,
      draftId,
    );
  }

  // Create draft document
  const draftData = formDataToDraftDTO(
    draftId,
    formData,
    filters,
    questions,
    evaluationQuestions,
    coverImageUrl,
    existingDraft || undefined,
  );

  // Save to Firestore
  const companyRef = doc(firestore, FirestoreCollection.Companies, companyId);
  const draftRef = doc(companyRef, DRAFTS_COLLECTION, draftId);

  await setDoc(draftRef, draftData);

  return draftData;
};

const getDrafts = async (companyId: string): Promise<DraftDTO[]> => {
  const companyRef = doc(firestore, FirestoreCollection.Companies, companyId);
  const draftsCollectionRef = collection(companyRef, DRAFTS_COLLECTION);

  const querySnapshot = await getDocs(draftsCollectionRef);

  const drafts: DraftDTO[] = querySnapshot.docs.map(doc => {
    const data = doc.data() as DraftDTO;
    return {
      ...data,
      id: doc.id,
    };
  });

  return drafts;
};

const getDraftById = async (
  companyId: string,
  draftId: string,
): Promise<DraftDTO | null> => {
  const companyRef = doc(firestore, FirestoreCollection.Companies, companyId);
  const draftRef = doc(companyRef, DRAFTS_COLLECTION, draftId);

  const draftDoc = await getDoc(draftRef);

  if (!draftDoc.exists()) {
    return null;
  }

  const data = draftDoc.data() as DraftDTO;
  return {
    ...data,
    id: draftDoc.id,
  };
};

const deleteDraft = async (
  companyId: string,
  draftId: string,
): Promise<void> => {
  // Delete image from storage
  await storage.deleteDraftImage(companyId, draftId);

  // Delete draft document from Firestore
  const companyRef = doc(firestore, FirestoreCollection.Companies, companyId);
  const draftRef = doc(companyRef, DRAFTS_COLLECTION, draftId);

  await deleteDoc(draftRef);
};

export const draft = {
  saveDraft,
  updateDraft,
  getDrafts,
  getDraftById,
  deleteDraft,
};
